import { useMutation, useQuery } from '@apollo/client';
import { digest } from 'jsum';
import { clone } from 'ramda';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LOAD_QUERY_QUERY } from '../usePersistedQuery/LoadQuery.gql';
import { SAVE_QUERY_MUTATION } from '../usePersistedQuery/SaveQuery.gql';
import { Domain, QueryParam } from '~/common/types';

export interface PersistedJsonSerde<T> {
  serialize: (value: T) => unknown;
  deserialize: (raw: unknown) => T | undefined;
}

interface PersistedState<T> {
  value?: T;
  id: string | null;
  checksum: string | null;
  modified?: boolean;
}

type UsePersistedJsonResult<T> = [T | undefined, (value: T | undefined) => void, { loading: boolean; error: unknown }];

const SAVE_DEBOUNCE_MS = 1000;

export const usePersistedJson = <T>(
  domain: Domain,
  q: string | undefined,
  serde: PersistedJsonSerde<T>,
): UsePersistedJsonResult<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PersistedState<T>>({ id: null, checksum: null });

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(LOAD_QUERY_QUERY, {
    variables: { id: q ?? '' },
    skip: !q,
  });

  const [save, { data: mutationData, loading: mutationLoading, error: mutationError, called }] =
    useMutation(SAVE_QUERY_MUTATION);

  useEffect(() => {
    if (!state.modified) return;
    const current = state.value;
    const timeout = setTimeout(() => {
      if (current === undefined) {
        setState({ id: null, checksum: null });
        const params = new URLSearchParams(searchParams);
        params.delete(QueryParam.QUERY);
        params.delete(QueryParam.ENCODED_QUERY);
        setSearchParams(params, { replace: true });
        return;
      }
      const payload = serde.serialize(current);
      const checksum = digest(clone(payload), 'SHA256', 'hex');
      if (checksum === state.checksum) return;
      setState((prev) => ({ ...prev, checksum, id: null }));
      save({ variables: { query: payload, checksum } });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value, state.modified]);

  useEffect(() => {
    if (called && !mutationLoading && !mutationError && mutationData?.insert_queries_one) {
      const id = mutationData.insert_queries_one.id;
      const params = new URLSearchParams(searchParams);
      params.set(QueryParam.QUERY, id);
      params.delete(QueryParam.ENCODED_QUERY);
      setState((prev) => ({ ...prev, id }));
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationData]);

  useEffect(() => {
    const row = queryData?.queries_by_pk;
    if (!row || row.id === state.id) return;
    const deserialized = serde.deserialize(row.query);
    setState({
      id: row.id,
      checksum: row.checksum ?? null,
      value: deserialized,
      modified: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData]);

  useEffect(() => {
    setState((prev) => (prev.id === q ? prev : { id: null, checksum: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, q]);

  const update = useCallback((next: T | undefined) => {
    setState((prev) => ({ ...prev, value: next, modified: true }));
  }, []);

  return [
    state.value,
    update,
    {
      loading: queryLoading || mutationLoading,
      error: queryError || mutationError,
    },
  ];
};
